import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchFeatureFlags, updateFeatureFlag } from '@/features/settings/api'
import type { FeatureFlagsResponse } from '@/features/settings/types'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'

const QUERY_KEY = ['feature-flags']

/**
 * Human-facing copy per known flag key — kept separate from the backend's
 * KNOWN_FEATURE_FLAGS set (admin.py) since this is presentation-only; a new
 * flag with no entry here still renders (falls back to its raw key) rather
 * than disappearing.
 */
const FLAG_COPY: Record<string, { title: string; description: string }> = {
  new_system_overview_dashboard_enabled: {
    title: 'System Overview Dashboard',
    description:
      "When enabled, users see the new visual System Overview as the default landing view for a system's Details page — the existing Daily/Weekly/Monthly/Yearly analytics experience remains fully available through its own tab. When disabled, the existing experience stays the default exactly as it behaves today.",
  },
  multi_node_support_enabled: {
    title: 'Multi-Node Fleet Display',
    description:
      'When enabled, sites with more than one registered Vision Node show a fleet-aggregated Site Overview with dynamic per-node tabs (PRD013). A single-node site behaves exactly as it does today regardless of this flag — the underlying node/equipment data foundation is always active; this flag only gates the new UI surface.',
  },
}

function formatUpdatedAt(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleString()
}

function FlagToggle({
  flagKey,
  value,
  onChange,
  disabled,
}: {
  flagKey: string
  value: boolean
  onChange: (next: boolean) => void
  disabled: boolean
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      aria-label={value ? `Disable ${flagKey}` : `Enable ${flagKey}`}
      disabled={disabled}
      onClick={() => onChange(!value)}
      className="relative inline-flex h-7 w-14 shrink-0 items-center rounded-full border border-border bg-bg-tertiary transition-colors duration-200 hover:border-brand/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-60"
    >
      <span
        aria-hidden="true"
        className={`relative z-10 inline-block h-6 w-6 rounded-full shadow transition-transform duration-200 ease-out ${
          value ? 'translate-x-[1.875rem] bg-brand' : 'translate-x-0.5 bg-card'
        }`}
      />
    </button>
  )
}

/**
 * Admin/staff-facing control for global platform settings (see backend
 * app/models.py's PlatformSetting) - initially just the System Overview
 * Dashboard rollout flag. Gated server-side by settings.feature_flags
 * (permission_required, not admin_only), so unlike Users Permissions itself
 * this page's own visibility can be delegated to Staff later from that
 * page, with no code change here.
 */
export default function FeatureFlagsPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchFeatureFlags,
  })
  const mutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: boolean }) => updateFeatureFlag(key, value),
    onMutate: async ({ key, value }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY })
      const previous = queryClient.getQueryData<FeatureFlagsResponse>(QUERY_KEY)
      queryClient.setQueryData<FeatureFlagsResponse>(QUERY_KEY, (old) =>
        old ? { flags: old.flags.map((f) => (f.key === key ? { ...f, value } : f)) } : old,
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(QUERY_KEY, context.previous)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
      // The flag also affects every other open tab's session-derived
      // default landing view - refresh that cache too so it doesn't take
      // up to a minute (session's staleTime) to notice.
      void queryClient.invalidateQueries({ queryKey: ['session'] })
    },
  })

  if (isLoading) return <LoadingState label="Loading feature flags…" />
  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : 'Failed to load feature flags.'}
        onRetry={() => refetch()}
      />
    )
  }
  if (!data) return null

  return (
    <div className="max-w-2xl">
      <h1 className="mb-1 text-xl font-semibold text-text-primary">Feature Flags</h1>
      <p className="mb-6 text-sm text-text-secondary">
        Global, persisted switches for the whole portal — changes here apply to every user immediately.
      </p>

      <div className="space-y-4">
        {data.flags.map((flag) => {
          const copy = FLAG_COPY[flag.key] ?? { title: flag.key, description: '' }
          const updatedAt = formatUpdatedAt(flag.updated_at)
          return (
            <div key={flag.key} className="rounded border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-sm font-semibold text-text-primary">{copy.title}</h2>
                  {copy.description && <p className="mt-1 text-sm text-text-secondary">{copy.description}</p>}
                  {(flag.updated_by || updatedAt) && (
                    <p className="mt-2 text-xs text-text-tertiary">
                      Last changed{updatedAt ? ` ${updatedAt}` : ''}
                      {flag.updated_by ? ` by ${flag.updated_by}` : ''}
                    </p>
                  )}
                </div>
                <FlagToggle
                  flagKey={flag.key}
                  value={flag.value}
                  disabled={mutation.isPending}
                  onChange={(next) => mutation.mutate({ key: flag.key, value: next })}
                />
              </div>
            </div>
          )
        })}
      </div>

      {mutation.isError && (
        <div className="mt-4">
          <ErrorState message={mutation.error instanceof Error ? mutation.error.message : 'Failed to update flag.'} />
        </div>
      )}
    </div>
  )
}
